<template>
    <div v-if="topic">
        <nav class="flex" aria-label="Breadcrumb">
            <ol role="list" class="flex items-center space-x-4">
                <li>
                    <div>
                        <a href="/" class="text-gray-400 hover:text-gray-500">
                            <svg
                                class="flex-shrink-0 h-6 w-6 feather feather-home"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                            <span class="sr-only">Home</span>
                        </a>
                    </div>
                </li>

                <li>
                    <div class="flex items-center">
                        <svg
                            class="flex-shrink-0 h-5 w-5 text-gray-400 feather feather-chevron-right"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                        <a href="/learn" class="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700" aria-current="page"
                            >Categories</a
                        >
                    </div>
                </li>

                <li>
                    <div class="flex items-center">
                        <svg
                            class="flex-shrink-0 h-5 w-5 text-gray-400 feather feather-chevron-right"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                        <a
                            :href="`/learn/${topic.category.topic.theme.slug}`"
                            class="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
                            aria-current="page"
                        >
                            {{ topic.category.topic.theme.name }}
                        </a>
                    </div>
                </li>

                <li>
                    <div class="flex items-center">
                        <svg
                            class="flex-shrink-0 h-5 w-5 text-gray-400 feather feather-chevron-right"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                        <p class="ml-4 text-sm font-medium text-gray-700">{{ topic.name }}</p>
                    </div>
                </li>
            </ol>
        </nav>
        <div
            class="mt-16 sm:mt-10 lg:mt-12 xl:mt-16 rounded-lg bg-white shadow-md border-transparent overflow-hidden h-full p-4 lg:p-8"
        >
            <div class="mb-12">
                <h2 class="mt-2 text-3xl sm:text-4xl font-bold">{{ topic.data.title }}</h2>
                <div class="mt-8 flex justify-between items-center gap-2 sm:gap-6">
                    <div class="flex items-center gap-4">
                        <div
                            class="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-full bg-color-2 bg-cover bg-no-repeat grayscale hover:grayscale-0 transition-all duration-300"
                            :style="{
                                backgroundImage: 'url(' + topic.authors[0].avatar + ')'
                            }"
                        ></div>
                        <div class="flex-1 text-gray-600 text-sm">
                            <h6 class="text-base lg:text-base text-gray-800 font-bold">{{ topic.authors[0].name }}</h6>
                            <p class="lg:mt-1">{{ topic.authors[0].bio }}</p>
                        </div>
                    </div>
                    <div class="flex gap-6 sm:gap-8 md:order-2">
                        <a :href="JSON.parse(socialMediaLinks).facebook" target="_blank" class=" text-primary">
                            <svg
                                class="h-5 sm:h-6 w-auto"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                            </svg>
                        </a>

                        <a :href="JSON.parse(socialMediaLinks).twitter" target="_blank" class=" text-primary">
                            <svg
                                class="h-5 sm:h-6 w-auto"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path
                                    d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"
                                ></path>
                            </svg>
                        </a>

                        <a :href="JSON.parse(socialMediaLinks).linkedin" target="_blank" class="text-primary">
                            <svg
                                class="h-5 sm:h-6 w-auto"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                                <rect x="2" y="9" width="4" height="12"></rect>
                                <circle cx="4" cy="4" r="2"></circle>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
            <div class="learn-content">
                <div v-for="(content, index) in topic.data.steps" :key="index" class="mb-4">
                    <!-- If there is a title in the section -->
                    <h3 v-if="content.title" class="text-lg sm:text-2xl font-bold">
                        {{ content.title }}
                    </h3>

                    <!-- If there is more than on html index in a content array -->
                    <template v-if="content.content > 1">
                        <template v-for="(innerContent, index2) in content.content">
                            <span :key="index2" v-html="innerContent"></span>
                        </template>
                    </template>
                    <template v-else>
                        <span v-html="content.content[0].html"></span>
                    </template>

                    <!-- If there is a checklist -->
                    <template v-if="content.content[0].checklist">
                        <template v-for="(checklist, index3) in content.content[0].checklist">
                            <div :key="index3" class="py-1 prose lg:prose-xl">
                                <label class="flex items-center">
                                    <input type="checkbox" class="form-checkbox text-primary" />
                                    <span class="ml-2 mr-2 leading-tight actionitem">
                                        <p dir="auto" v-html="checklist.label"></p>
                                    </span>
                                </label>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
    <div v-else>
        <p class="text-center text-gray-500">It is loading Loading...</p>
    </div>
</template>

<script>
export default {
    props: {
        topicSlug: {
            type: String,
            required: true
        },
        categorySlug: {
            type: String,
            required: true
        },
        socialMediaLinks: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            topic: null
        };
    },

    mounted() {
        this.fetchTopic();
    },

    methods: {
        fetchTopic() {
            fetch(`${process.env.MIX_RUMIE_BASE_URL}/api/v3/learn/projects/1/bytes/${this.topicSlug}`)
                .then(response => response.json())
                .then(response => {
                    this.topic = response;
                });
        }
    }
};
</script>
